import { PropertyTypeEnum } from '~/core/entities/CRMEnums';
import { PropertyFiltersPresenter } from '~/presenters/PropertyFiltersPresenter';

export { default as PoiAddressGridSection } from './Section.vue';

export interface LinkGroup {
	title: string;
	items: { label: string; href: string }[];
}

interface PoiAddress {
	displayName: string;
	formattedAddress: string;
	lat: number;
	lng: number;
}

interface PoiAddressGroupConfig {
	title: string;
	propertyType?: PropertyTypeEnum;
	addresses: PoiAddress[];
}

interface CityConfig {
	name: string;
	uf: string;
}

const PROPERTY_TYPE_LABEL_MAP: Partial<Record<PropertyTypeEnum, string>> = {
	[PropertyTypeEnum.APARTMENT]: 'Apartamentos',
	[PropertyTypeEnum.HOUSE]: 'Casas',
	[PropertyTypeEnum.CONDO_HOUSE]: 'Casas de condomínio'
};

function buildPoiUrl(
	address: PoiAddress,
	city: CityConfig,
	propertyType?: PropertyTypeEnum
): string {
	return PropertyFiltersPresenter.getPathFromFilters({
		transactionType: 'sell',
		addressSlug: address.formattedAddress,
		poiLocationLat: address.lat,
		poiLocationLng: address.lng,
		states: [city.uf],
		...(propertyType ? { propertyTypes: [propertyType] } : {})
	});
}

function buildLabel(
	displayName: string,
	propertyType?: PropertyTypeEnum
): string {
	if (!propertyType) {
		return displayName;
	}
	const propertyTypeLabel = PROPERTY_TYPE_LABEL_MAP[propertyType] || '';
	return `${propertyTypeLabel} em ${displayName}`;
}

function createLinkGroup(
	config: PoiAddressGroupConfig,
	city: CityConfig
): LinkGroup {
	return {
		title: config.title,
		items: config.addresses.map(address => ({
			label: buildLabel(address.displayName, config.propertyType),
			href: buildPoiUrl(address, city, config.propertyType)
		}))
	};
}

// São Paulo configuration
const saoPauloCity: CityConfig = {
	name: 'São Paulo',
	uf: 'SP'
};

const saoPauloGroupConfigs: PoiAddressGroupConfig[] = [
	{
		title: 'Imóveis próximos a ruas',
		addresses: [
			{
				displayName: 'Rua Haddock Lobo',
				formattedAddress: 'Rua Haddock Lobo, Cerqueira César, São Paulo - SP',
				lat: -23.5616476,
				lng: -46.6656018
			},
			{
				displayName: 'Rua Bela Cintra',
				formattedAddress: 'Rua Bela Cintra, Consolação, São Paulo - SP',
				lat: -23.5532685,
				lng: -46.6576288
			},
			{
				displayName: 'Avenida Juriti',
				formattedAddress: 'Avenida Juriti, Moema, São Paulo - SP',
				lat: -23.5999905,
				lng: -46.6642658
			},
			{
				displayName: 'Rua Joaquim Antunes',
				formattedAddress: 'Rua Joaquim Antunes, Pinheiros, São Paulo - SP',
				lat: -23.5641584,
				lng: -46.6817144
			},
			{
				displayName: 'Rua Tucumã',
				formattedAddress: 'Rua Tucumã, Jardim Europa, São Paulo - SP',
				lat: -23.5817325,
				lng: -46.6895259
			}
		]
	},
	{
		title: 'Apartamentos próximos às ruas',
		propertyType: PropertyTypeEnum.APARTMENT,
		addresses: [
			{
				displayName: 'Rua Oscar Freire',
				formattedAddress: 'Rua Oscar Freire, Jardim Paulista, São Paulo - SP',
				lat: -23.5686704,
				lng: -46.6626754
			},
			{
				displayName: 'Rua Haddock Lobo',
				formattedAddress: 'Rua Haddock Lobo, Cerqueira César, São Paulo - SP',
				lat: -23.5616476,
				lng: -46.6656018
			},
			{
				displayName: 'Rua Bela Cintra',
				formattedAddress: 'Rua Bela Cintra, Consolação, São Paulo - SP',
				lat: -23.5532685,
				lng: -46.6576288
			},
			{
				displayName: 'Rua Pernambuco',
				formattedAddress: 'Rua Pernambuco, Jardim Paulista, São Paulo - SP',
				lat: -23.5668185,
				lng: -46.6624944
			},
			{
				displayName: 'Rua Venezuela',
				formattedAddress: 'Rua Venezuela, Jardim Paulista, São Paulo - SP',
				lat: -23.5655917,
				lng: -46.6713675
			}
		]
	},
	{
		title: 'Casas próximas às ruas',
		propertyType: PropertyTypeEnum.HOUSE,
		addresses: [
			{
				displayName: 'Rua Estados Unidos',
				formattedAddress: 'Rua Estados Unidos, Jardim America, São Paulo - SP',
				lat: -23.5670894,
				lng: -46.6683854
			},
			{
				displayName: 'Rua Tabapuã',
				formattedAddress: 'Rua Tabapuã, Itaim Bibi, São Paulo - SP',
				lat: -23.5839916,
				lng: -46.6789935
			},
			{
				displayName: 'Rua Pernambuco',
				formattedAddress: 'Rua Pernambuco, Higienópolis, São Paulo - SP',
				lat: -23.5428842,
				lng: -46.6613907
			},
			{
				displayName: 'Rua Curitiba',
				formattedAddress: 'Rua Curitiba, Paraíso, São Paulo - SP',
				lat: -23.5812737,
				lng: -46.6541037
			},
			{
				displayName: 'Rua Sergipe',
				formattedAddress: 'Rua Sergipe, Higienópolis, São Paulo - SP',
				lat: -23.5474736,
				lng: -46.6589
			}
		]
	},
	{
		title: 'Imóveis próximos a pontos de interesse',
		addresses: [
			{
				displayName: 'Imóveis próximos à Praça Pereira Coutinho',
				formattedAddress:
					'Praça Pereira Coutinho, Vila Nova Conceição, São Paulo - SP',
				lat: -23.593161,
				lng: -46.669434
			},
			{
				displayName: 'Imóveis próximos ao Parque do Ibirapuera',
				formattedAddress: 'Parque Ibirapuera, Vila Mariana, São Paulo - SP',
				lat: -23.5874,
				lng: -46.6576
			},
			{
				displayName: 'Imóveis próximos ao Clube Athletico Paulistano',
				formattedAddress:
					'Club Athletico Paulistano, Jardim América, São Paulo - SP',
				lat: -23.568386,
				lng: -46.668963
			},
			{
				displayName: 'Imóveis próximos ao Parque do Povo',
				formattedAddress:
					'Parque do Povo Mário Pimenta Camargo, Avenida Henrique Chamma - Pinheiros, São Paulo - SP',
				lat: -23.587594,
				lng: -46.688417099999995
			},
			{
				displayName: 'Imóveis próximos ao Parque Villa-Lobos',
				formattedAddress:
					'Parque Villa-Lobos, Avenida Professor Fonseca Rodrigues - Alto de Pinheiros, São Paulo - SP',
				lat: -23.5463088,
				lng: -46.725375799999995
			}
		]
	}
];

// Curitiba configuration
const curitibaCity: CityConfig = {
	name: 'Curitiba',
	uf: 'PR'
};

const curitibaGroupConfigs: PoiAddressGroupConfig[] = [
	{
		title: 'Imóveis próximos a ruas',
		addresses: [
			{
				displayName: 'Rua Brigadeiro Franco',
				formattedAddress: 'Rua Brigadeiro Franco, Água Verde, Curitiba - PR',
				lat: -25.4534129,
				lng: -49.2709882
			},
			{
				displayName: 'Rua Prof. Pedro Viriato Parigot de Souza',
				formattedAddress:
					'Rua Professor Pedro Viriato Parigot de Souza, Mossunguê, Curitiba - PR',
				lat: -25.4362459,
				lng: -49.3252714
			},
			{
				displayName: 'Av. Visconde de Guarapuava',
				formattedAddress: 'Av. Visc. de Guarapuava, Centro, Curitiba - PR',
				lat: -25.4361517,
				lng: -49.2672033
			},
			{
				displayName: 'Rua Dep. Heitor Alencar Furtado',
				formattedAddress:
					'Rua Deputado Heitor Alencar Furtado, Campina do Siqueira, Curitiba - PR',
				lat: -25.4378521,
				lng: -49.3133185
			},
			{
				displayName: 'Rua José Izidoro Biazetto',
				formattedAddress: 'Rua José Izidoro Biazetto, Mossunguê, Curitiba - PR',
				lat: -25.4308742,
				lng: -49.3388725
			}
		]
	},
	{
		title: 'Apartamentos próximos às ruas',
		propertyType: PropertyTypeEnum.APARTMENT,
		addresses: [
			{
				displayName: 'Rua Prof. Pedro Viriato Parigot de Souza',
				formattedAddress:
					'Rua Professor Pedro Viriato Parigot de Souza, Mossunguê, Curitiba - PR',
				lat: -25.4362459,
				lng: -49.3252714
			},
			{
				displayName: 'Rua Guilherme Pugsley',
				formattedAddress: 'Rua Guilherme Pugsley, Água Verde, Curitiba - PR',
				lat: -25.4589039,
				lng: -49.2871862
			},
			{
				displayName: 'Rua José Izidoro Biazetto',
				formattedAddress: 'Rua José Izidoro Biazetto, Mossunguê, Curitiba - PR',
				lat: -25.4308742,
				lng: -49.3388725
			},
			{
				displayName: 'Rua Martim Afonso',
				formattedAddress: 'Rua Martim Afonso, Bigorrilho, Curitiba - PR',
				lat: -25.4328735,
				lng: -49.2960704
			},
			{
				displayName: 'Rua Mariano Torres',
				formattedAddress: 'Rua Mariano Torres, Centro, Curitiba - PR',
				lat: -25.4314227,
				lng: -49.2621961
			}
		]
	},
	{
		title: 'Casas de condomínio próximas às ruas',
		propertyType: PropertyTypeEnum.CONDO_HOUSE,
		addresses: [
			{
				displayName: 'Rua Luiz Tramontin',
				formattedAddress: 'Rua Luiz Tramontin, Campo Comprido, Curitiba - PR',
				lat: -25.4555362,
				lng: -49.3337029
			},
			{
				displayName: 'Rua Luiz Zilli',
				formattedAddress: 'Rua Luiz Zilli, Campo Comprido, Curitiba - PR',
				lat: -25.4554729,
				lng: -49.3252483
			},
			{
				displayName: 'Rua Hermenegildo Luca',
				formattedAddress: 'Rua Hermenegildo Luca, São Braz, Curitiba - PR',
				lat: -25.4103741,
				lng: -49.3611077
			},
			{
				displayName: 'Rua José Benedito Cottolengo',
				formattedAddress:
					'Rua José Benedito Cottolengo, Campo Comprido, Curitiba - PR',
				lat: -25.4645328,
				lng: -49.3259817
			},
			{
				displayName: 'Rua Tobias de Macedo Júnior',
				formattedAddress:
					'Rua Tobias de Macedo Júnior, Santo Inácio, Curitiba - PR',
				lat: -25.4272583,
				lng: -49.3292898
			}
		]
	},
	{
		title: 'Imóveis próximos a pontos de interesse',
		addresses: [
			{
				displayName: 'Imóveis próximos ao Jardim Botânico',
				formattedAddress:
					'Jardim Botânico de Curitiba, Rua Engenheiro Ostoja Roguski - Jardim Botânico, Curitiba - PR',
				lat: -25.443121299999998,
				lng: -49.2382445
			},
			{
				displayName: 'Imóveis próximos ao Parque Barigui',
				formattedAddress:
					'Parque Barigui, Avenida Cândido Hartmann - Bigorrilho, Curitiba - PR',
				lat: -25.4262654,
				lng: -49.30723
			},
			{
				displayName: 'Imóveis próximos ao Parque Tanguá',
				formattedAddress:
					'Parque Tanguá, Rua Oswaldo Maciel - Taboão, Curitiba - PR',
				lat: -25.3790661,
				lng: -49.282423300000005
			},
			{
				displayName: 'Imóveis próximos ao Parque Tingui',
				formattedAddress:
					'Parque Tingui, Avenida Fredolin Wolf - Pilarzinho, Curitiba - PR',
				lat: -25.4024187,
				lng: -49.305336399999995
			},
			{
				displayName: 'Imóveis próximos ao Pátio Batel',
				formattedAddress:
					'Shopping Pátio Batel, Avenida do Batel - Batel, Curitiba - PR',
				lat: -25.442929499999998,
				lng: -49.290813299999996
			}
		]
	}
];

// Generate link groups
export const saoPauloGroups: LinkGroup[] = saoPauloGroupConfigs.map(config =>
	createLinkGroup(config, saoPauloCity)
);

export const curitibaGroups: LinkGroup[] = curitibaGroupConfigs.map(config =>
	createLinkGroup(config, curitibaCity)
);
